import { Avatar } from "@mui/material";
import { useEffect, useRef } from "react";
import { FlexCenterWrapper } from "../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../components/Flex";
import Text from "../../../../../components/Text";
import { formatTime } from "../../../../../utils/date-time-utility";
import { getInitials, getLightBgColor } from "../../../../../utils/utils";
import DateStrip from "../../patient/patient-list/patient-chart-new/patient-chronological-records-and-requiring-actions/DateStrip";
import noMessageImg from "/default-images/no-message.svg";
import ChatBubble from "../messages/ChatBubble";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import { useTheme } from "@mui/material";

type UserKeys =
  | "patientId"
  | "patientName"
  | "providerColorCode"
  | "providerId"
  | "providerName"
  | "sendDate"
  | "sendBy"
  | "smsContent"
  | "uuid"
  | "patientColor"
  | "attachment_name"
  | "attachment"
  | "isSender"
  | "content";

type User = {
  [k in UserKeys]: string;
};

type Messages = Record<string, User[]>;

type ChatBoxProps = {
  messages?: Messages;
  senderName?: string;
};

const ChatBox = ({ messages, senderName }: ChatBoxProps) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [me] = useCurrentUser();

  useEffect(() => {
    bottomRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [messages]);

  const isMessages = Object.keys(messages ?? {}).length > 0;
  const theme = useTheme();

  return !!isMessages ? (
    Object.entries(messages!)?.map(([date, dateMessages]) => {
      return (
        <div key={date}>
          <DateStrip date={date} />

          {dateMessages?.map((it) => {
            // console.log("chat message val here check it", it);
            const isSender = it?.sendBy !== me?.uuid;

            return (
              <Flex
                key={it?.uuid}
                width={"100%"}
                justifyContent={isSender ? "start" : "end"}
                gap={2}
                mt={4}
              >
                <Flex gap={1} width={"55%"}>
                  {isSender && <UserAvatar name={senderName} />}
                  <Flex
                    flexDirection={"column"}
                    width={"100%"}
                    alignItems={isSender ? "start" : "end"}
                  >
                    <Flex
                      justifyContent={isSender ? "start" : "end"}
                      width={"100%"}
                      height={"20px"}
                    >
                      <Flex gap={1} height={"15px"}>
                        <Text fontSize={"12px"}>
                          {/* {senderName} */}
                          {isSender ? it?.patientName : it?.providerName}
                        </Text>
                        <Text fontSize={"12px"}>
                          {formatTime(it?.sendDate)}
                        </Text>
                      </Flex>
                    </Flex>
                    {/* todo need to unComment for multiple message at one time */}
                    {/* {it?.smsContent?.map((msg) => ( */}
                    <ChatBubble
                      color={
                        isSender
                          ? theme.palette.primary.light
                          : it?.providerColorCode
                      }
                      message={it?.content}
                      isSender={!!isSender}
                      attachment={it?.attachment}
                      attachmentExt={(it as any)?.attachment_ext}
                      fileName={it?.attachment_name}
                    />
                  </Flex>
                </Flex>
              </Flex>
            );
          })}
          <div ref={bottomRef} />
        </div>
      );
    })
  ) : (
    <NoMessageAvailable />
  );
};

export default ChatBox;

type UserAvatarProps = {
  name?: string;
  size?: string;
};

// Same initials + color scheme as the conversation header/list (ChatUser),
// so a person's avatar looks identical everywhere in the chat.
const UserAvatar = ({ name, size = "30px" }: UserAvatarProps) => {
  const colors = getLightBgColor(name ?? "");
  return (
    <Avatar
      sx={{
        bgcolor: colors.bg,
        color: colors.text,
        height: size,
        width: size,
        fontSize: "0.8rem",
      }}
    >
      {getInitials(name ?? "")}
    </Avatar>
  );
};

const NoMessageAvailable = () => (
  <FlexCenterWrapper>
    <img src={noMessageImg} alt={"No Message Available"} />
    <Text fontSize={"16px"}>No messages yet, Start the conversation ! </Text>
  </FlexCenterWrapper>
);
